import { useTranslation } from 'react-i18next';
import { Dialog } from '@/components/ui/organisms/Dialog';
import { Button } from '@/components/ui/atoms/Button';
import { Typography } from '@/components/ui/atoms';
import { getGTMAttributes } from '@/utils/gtm/gtm-tracking';
import {
  getConfirmationDialogButtonTracking,
  getConfirmationDialogCloseTracking,
} from '@/utils/gtm/gtm-confirmation-dialog';
import { SendNotificationCheckbox } from './SendNotificationCheckbox';
import { CandidateIdentity } from './CandidateIdentity';

interface DeclineApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  candidateName?: string;
  candidateEmail?: string | null;
  isPending: boolean;
  origin: string;
  /** Destination stage would notify; the email checkbox renders only then. */
  willNotify: boolean;
  sendNotification: boolean;
  onSendNotificationChange: (value: boolean) => void;
}

export function DeclineApplicationDialog({
  open,
  onClose,
  onConfirm,
  candidateName,
  candidateEmail,
  isPending,
  origin,
  willNotify,
  sendNotification,
  onSendNotificationChange,
}: DeclineApplicationDialogProps) {
  const { t } = useTranslation('candidates');
  const gtmConfig = { gtm: 'DECLINE_CANDIDATE' as const, origin };
  const title = t('meta_cv.reject_dialog.title');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      title={title}
    >
      <Dialog.Header
        titleVariant="h3"
        title={title}
        showCloseButton
        closeButtonProps={getGTMAttributes(getConfirmationDialogCloseTracking(gtmConfig))}
      />
      <Dialog.Body className="space-y-4">
        <CandidateIdentity
          name={candidateName?.trim() || t('meta_cv.reject_dialog.candidate_fallback')}
          email={candidateEmail}
        />
        {willNotify ? (
          <SendNotificationCheckbox
            id="send-rejection-notification"
            label={t('meta_cv.reject_dialog.send_notification')}
            checked={sendNotification}
            onChange={onSendNotificationChange}
            disabled={isPending}
          />
        ) : (
          <Typography variant="body-sm" color="secondary" as="p">
            {t('meta_cv.reject_dialog.consequence_off')}
          </Typography>
        )}
      </Dialog.Body>
      <Dialog.Footer align="right">
        <Button
          {...getGTMAttributes(
            getConfirmationDialogButtonTracking(gtmConfig, {
              intent: 'secondary',
              isLastButton: false,
            })
          )}
          type="outlined"
          intent="secondary"
          onClick={onClose}
          disabled={isPending}
          label={t('meta_cv.reject_dialog.cancel')}
          data-autofocus
        />
        <Button
          {...getGTMAttributes(
            getConfirmationDialogButtonTracking(gtmConfig, {
              intent: 'danger',
              isLastButton: true,
            })
          )}
          type="contained"
          intent="danger"
          onClick={onConfirm}
          disabled={isPending}
          loading={isPending}
          label={t('meta_cv.reject_dialog.decline')}
        />
      </Dialog.Footer>
    </Dialog>
  );
}
